import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';
import { io } from 'socket.io-client';
import L from 'leaflet';
import truckImg from '../../assets/image/delivery-truck.png';
import LeafletDriversMap from '../../components/maps/LeafletDriversMap';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
// const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
// const USE_GOOGLE_MAPS = String(import.meta.env.VITE_USE_GOOGLE_MAPS || 'false').toLowerCase() == 'true';
const USE_GOOGLE_MAPS = false;
const ANIMATION_DURATION_MS = 800;
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const DEFAULT_CENTER = { lat: 23.0225, lng: 72.5714 };
const MAP_OPTIONS = { fullscreenControl: false, streetViewControl: false, mapTypeControl: false };

const GoogleDriversMap = ({
  apiKey,
  driverEntries,
  markerPositions,
  drivers,
  selectedDriverId,
  setSelectedDriverId,
  onGoogleMapLoad,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey });

  if (!apiKey) {
    return (
      <div style={S.centered}>
        <div style={{ fontSize: 14, color: '#666' }}>Google Maps disabled (missing API key).</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={S.centered}>
        <div style={{ fontSize: 14, color: '#d4380d', fontWeight: 600 }}>Google Maps failed to load.</div>
        <div style={{ ...S.centeredSub, marginBottom: 0 }}>Check API key, billing, and referrer restrictions.</div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={S.centered}>
        <div style={{ fontSize: 14, color: '#666' }}>Loading map…</div>
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={MAP_CONTAINER_STYLE}
      center={DEFAULT_CENTER}
      zoom={12}
      onLoad={onGoogleMapLoad}
      options={MAP_OPTIONS}
    >
      {driverEntries.map(([driverIdStr]) => {
        const driverId = Number(driverIdStr);
        const pos = markerPositions[driverId];
        const driver = drivers[driverId];
        if (!pos || !driver) return null;
        const idStr = String(driverId);
        const isSelected = selectedDriverId === idStr;

        return (
          <Marker
            key={driverId}
            position={pos}
            icon={{
              url: truckImg,
              scaledSize: new window.google.maps.Size(44, 44),
              anchor: new window.google.maps.Point(22, 22),
            }}
            // Live = full opacity, offline = faded
            opacity={driver.is_live ? 1 : 0.45}
            title={driver.driver_name}
            tracksViewChanges={false}
            onClick={() => setSelectedDriverId(isSelected ? null : idStr)}
          >
            {isSelected && (
              <InfoWindow position={pos} onCloseClick={() => setSelectedDriverId(null)}>
                <div style={{ minWidth: 140 }}>
                  <div style={S.infoName}>{driver.driver_name}</div>
                  <div style={S.infoStatus(driver.is_live)}>
                    {driver.is_live ? '● Live' : `● Offline · last seen ${timeAgo(driver.last_updated)}`}
                  </div>
                  {driver.last_updated && (
                    <div style={S.infoTime}>
                      {driver.is_live ? 'Updated' : 'Last'}: {new Date(driver.last_updated).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </InfoWindow>
            )}
          </Marker>
        );
      })}
    </GoogleMap>
  );
};

function resolveSocket(apiUrl) {
  const url = apiUrl.replace(/\/$/, '');
  if (url.endsWith('/admin')) return { origin: url.slice(0, -6), socketPath: '/admin/socket.io' };
  return { origin: url, socketPath: '/socket.io' };
}

function alternateSocketPath(path) {
  return path === '/admin/socket.io' ? '/socket.io' : '/admin/socket.io';
}

function timeAgo(ts) {
  if (!ts) return null;
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function toTitleCaseStatus(s) {
  if (!s) return '';
  return String(s).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildMobileSteps(orderStatus, shipmentStatus) {
  // Use backend statuses:
  // - order_status (Order.orderStatus)
  // - status (Shipment.shipmentStatus) from live-drivers API
  // Shipment status should be the primary driver of the "timeline".
  const os = String(orderStatus || '').toUpperCase();
  const ss = String(shipmentStatus || '').toUpperCase();

  // Prefer shipment status if present; otherwise fall back to order status.
  const cur = ss || os || 'PENDING';

  const rank = (s) => {
    switch (String(s || '').toUpperCase()) {
      case 'PENDING_ACCEPTANCE': return 0;
      case 'PENDING': return 0;
      case 'CONFIRMED': return 1;
      case 'PROCESSING': return 1;
      case 'READY_FOR_PICKUP': return 2;
      case 'PICKED_UP': return 3;
      case 'IN_TRANSIT': return 4;
      case 'OUT_FOR_DELIVERY': return 5;
      case 'DELIVERED': return 6;
      case 'COMPLETED': return 6;
      case 'CANCELLED': return -1;
      case 'FAILED': return -1;
      case 'RETURNED': return -1;
      case 'REFUNDED': return -1;
      default: return 0;
    }
  };

  const r = rank(cur);
  const isCancelled = r === -1;

  // 4-step timeline like the mock (without extra buttons).
  const steps = [
    { key: 'confirmed', title: 'Order Confirmed', threshold: 1 },
    { key: 'ready', title: 'Ready for Pickup', threshold: 2 },
    { key: 'out', title: 'Out for Delivery', threshold: 5 },
    { key: 'delivered', title: 'Delivered', threshold: 6 },
  ];

  return steps.map((s) => {
    const done = !isCancelled && r >= s.threshold;
    const current = !isCancelled && !done && r < s.threshold && (s.threshold === 1 ? true : r >= (s.threshold - 1));
    return {
      ...s,
      done,
      current,
      sub: isCancelled ? toTitleCaseStatus(cur) : (cur ? toTitleCaseStatus(cur) : ''),
    };
  });
}

// ── Styles ───────────────────────────────────────────────────────────────────

const S = {
  root: { height: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f2f5', overflow: 'hidden' },

  header: {
    background: '#1677ff', color: '#fff',
    padding: '12px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(22,119,255,0.3)',
  },
  headerTitle: { fontSize: 16, fontWeight: 700, margin: 0 },
  headerSub: { fontSize: 12, opacity: 0.82, marginTop: 2 },
  statusDot: (connected) => ({
    fontSize: 12, fontWeight: 600,
    background: connected ? 'rgba(82,196,26,0.9)' : 'rgba(255,77,79,0.9)',
    padding: '4px 12px', borderRadius: 20,
  }),
  mapWrap: { flex: 1, position: 'relative', minHeight: 0 },
  centered: {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: '#f5f5f5',
  },
  centeredTitle: { fontSize: 18, color: '#333', marginBottom: 8 },
  centeredSub: { fontSize: 13, color: '#999', marginBottom: 16 },
  retryBtn: {
    padding: '8px 24px', borderRadius: 8, border: 'none',
    background: '#1677ff', color: '#fff', fontSize: 14, cursor: 'pointer',
  },

  noDriversToast: {
    position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.7)', color: '#fff',
    padding: '10px 20px', borderRadius: 20, fontSize: 13, whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },

  driverPanel: {
    background: '#fff', borderTop: '1px solid #f0f0f0',
    maxHeight: 200, overflowY: 'auto', flexShrink: 0,
  },
  driverCard: (clickable) => ({
    display: 'flex', alignItems: 'center',
    padding: '12px 16px', borderBottom: '1px solid #f5f5f5',
    cursor: clickable ? 'pointer' : 'default',
  }),
  avatar: (isLive) => ({
    width: 38, height: 38, borderRadius: '50%',
    background: isLive ? '#1677ff' : '#bfbfbf',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 700, fontSize: 15,
    flexShrink: 0, marginRight: 12,
  }),
  cardName: { fontWeight: 600, fontSize: 14, color: '#1e293b' },
  liveBadge: {
    display: 'inline-block', fontSize: 10, fontWeight: 700,
    background: '#e6ffe6', color: '#389e0d',
    padding: '1px 6px', borderRadius: 8, marginLeft: 6, verticalAlign: 'middle',
  },
  offlineBadge: {
    display: 'inline-block', fontSize: 10, fontWeight: 600,
    background: '#f5f5f5', color: '#8c8c8c',
    padding: '1px 6px', borderRadius: 8, marginLeft: 6, verticalAlign: 'middle',
  },
  cardCoord: (hasGps) => ({ fontSize: 12, color: hasGps ? '#595959' : '#fa8c16', marginTop: 2, fontFamily: 'monospace' }),
  cardLastSeen: { fontSize: 11, color: '#8c8c8c', marginTop: 2 },
  shipmentTag: { fontSize: 11, color: '#8c8c8c', marginTop: 2 },

  infoName: { fontWeight: 700, fontSize: 14, marginBottom: 4 },
  infoStatus: (isLive) => ({ fontSize: 11, color: isLive ? '#52c41a' : '#8c8c8c', marginBottom: 4 }),
  infoTime: { fontSize: 11, color: '#999' },
};

// ── CustomerLiveMap ───────────────────────────────────────────────────────────

const CustomerLiveMap = () => {
  // Raw value from URL — can be internal Order.id OR Shopify Order GID numeric tail
  // (e.g. "1645837152" from "gid://shopify/Order/1645837152").
  // Backend resolves both; we use the internal id it returns for the socket room.
  const rawOrderParam = String(new URLSearchParams(window.location.search).get('order_id') || '').trim();
  const [orderId, setOrderId] = useState(null); // internal Order.id, set after REST resolves

  const [pageState, setPageState] = useState('loading'); // loading | planInactive | notFound | error | live
  const [orderStatus, setOrderStatus] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [drivers, setDrivers] = useState({});           // { [driver_id]: { ...data, is_live } }
  const [markerPositions, setMarkerPositions] = useState({}); // { [driver_id]: { lat, lng } } — RAF-driven
  // Store as string to match Object.entries() keys
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);

  const mapRef = useRef(null);
  const prevGpsPos = useRef({});
  const animFrames = useRef({});
  const didInitialFocus = useRef(false);
  const [mapReadyTick, setMapReadyTick] = useState(0);

  const onGoogleMapLoad = useCallback((map) => { mapRef.current = map; }, []);
  const onLeafletMapCreated = useCallback((map) => {
    mapRef.current = map;
    // Trigger effects that need a ready map instance
    setMapReadyTick((n) => n + 1);
  }, []);

  const getValidLatLng = useCallback((d) => {
    const lat = Number(d?.lat);
    const lng = Number(d?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, []);

  const focusOnLatLng = useCallback((ll) => {
    if (!ll || !mapRef.current) return;
    if (USE_GOOGLE_MAPS) {
      mapRef.current.panTo({ lat: ll.lat, lng: ll.lng });
      mapRef.current.setZoom(15);
    } else {
      mapRef.current.setView([ll.lat, ll.lng], 15, { animate: true });
    }
  }, []);

  // Initial zoom: focus on first driver with GPS as soon as we have positions.
  useEffect(() => {
    if (didInitialFocus.current) return;
    if (!mapRef.current) return;
    const keys = Object.keys(markerPositions || {});
    if (keys.length === 0) return;
    const firstKey = String(selectedDriverId || keys[0]);
    const pos = markerPositions[firstKey] || markerPositions[keys[0]];
    const ll = getValidLatLng(pos);
    if (!ll) return;
    didInitialFocus.current = true;
    setSelectedDriverId(firstKey);
    // Leaflet can render slightly after whenCreated; a small delay makes autofocus reliable.
    setTimeout(() => focusOnLatLng(ll), 150);
  }, [markerPositions, selectedDriverId, getValidLatLng, focusOnLatLng, mapReadyTick]);

  // Ensure "click driver card" always focuses even if map is created slightly later.
  useEffect(() => {
    if (!selectedDriverId || !mapRef.current) return;
    const d = drivers[selectedDriverId];
    const ll = getValidLatLng(d);
    focusOnLatLng(ll);
  }, [selectedDriverId, drivers, getValidLatLng, focusOnLatLng]);

  // ── RAF animation (ease-out cubic) — only for live drivers ──────────────
  const animateMarker = useCallback((driverId, fromLat, fromLng, toLat, toLng) => {
    if (animFrames.current[driverId]) cancelAnimationFrame(animFrames.current[driverId]);
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / ANIMATION_DURATION_MS, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setMarkerPositions((prev) => ({
        ...prev,
        [driverId]: {
          lat: fromLat + (toLat - fromLat) * ease,
          lng: fromLng + (toLng - fromLng) * ease,
        },
      }));
      if (t < 1) animFrames.current[driverId] = requestAnimationFrame(step);
      else delete animFrames.current[driverId];
    };
    animFrames.current[driverId] = requestAnimationFrame(step);
  }, []);

  // ── Fit map to all drivers with GPS ─────────────────────────────────────
  const fitToDrivers = useCallback((driversMap) => {
    const map = mapRef.current;
    const valid = Object.values(driversMap).filter((d) => d.lat && d.lng);
    if (!valid.length) return;
    if (!map) return;

    if (USE_GOOGLE_MAPS) {
      if (!window.google) return;
      if (valid.length === 1) {
        map.panTo({ lat: valid[0].lat, lng: valid[0].lng });
        map.setZoom(15);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        valid.forEach((d) => bounds.extend({ lat: d.lat, lng: d.lng }));
        map.fitBounds(bounds, 60);
      }
      return;
    }

    // Leaflet fit (no API key)
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 15, { animate: true });
    } else {
      const bounds = L.latLngBounds(valid.map((d) => [d.lat, d.lng]));
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, []);

  // ── Phase A: REST fetch — initial snapshot (live + offline last positions) ─
  useEffect(() => {
    if (!rawOrderParam) { setPageState('notFound'); return; }

    fetch(`${API_URL}/api/order/live-drivers/${encodeURIComponent(rawOrderParam)}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) {
          if (data.code === 'NO_ACTIVE_PLAN' || data.code === 'PLAN_EXPIRED') {
            setPageState('planInactive');
          } else {
            setPageState('notFound');
          }
          return;
        }
        setOrderStatus(data.order_status);
        setDeliveryAddress(data.delivery_address || null);
        // Backend returns the internal Order.id — use this for the socket room
        setOrderId(Number(data.order_id));

        const initDrivers = {};
        const initPositions = {};
        for (const d of data.drivers) {
          initDrivers[d.driver_id] = d;
          if (d.lat && d.lng) {
            initPositions[d.driver_id] = { lat: d.lat, lng: d.lng };
            prevGpsPos.current[d.driver_id] = { lat: d.lat, lng: d.lng };
          }
        }
        // If refresh returns empty drivers transiently (status propagation / socket reconnect),
        // keep the last known drivers on screen to avoid UI "blinking".
        setDrivers((prev) => {
          const next = { ...(prev || {}) };
          const keys = Object.keys(initDrivers || {});
          if (keys.length === 0) return next;
          keys.forEach((k) => { next[k] = initDrivers[k]; });
          return next;
        });
        setMarkerPositions((prev) => {
          const next = { ...(prev || {}) };
          const keys = Object.keys(initPositions || {});
          if (keys.length === 0) return next;
          keys.forEach((k) => { next[k] = initPositions[k]; });
          return next;
        });
        setPageState('live');
        setTimeout(() => fitToDrivers(initDrivers), 300);
      })
      .catch(() => setPageState('error'));
  }, [rawOrderParam, fitToDrivers]);

  // ── Phase B: Socket — real-time updates ────────────────────────────────
  useEffect(() => {
    if (!orderId) return;

    const { origin, socketPath } = resolveSocket(API_URL);
    let attemptedFallback = false;
    let socket = null;

    const connectWithPath = (path) => {
      socket = io(`${origin}/tracking`, {
        path,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
      });

      socket.on('connect', () => {
        setSocketConnected(true);
        socket.emit('joinOrderTracking', { orderId });
      });
      socket.on('disconnect', () => setSocketConnected(false));

      socket.on('connect_error', (e) => {
        // If proxy/backend only supports the other Socket.IO path, retry once.
        if (!attemptedFallback) {
          attemptedFallback = true;
          const nextPath = alternateSocketPath(path);
          try {
            socket.removeAllListeners();
            socket.disconnect();
          } catch (_) {}
          connectWithPath(nextPath);
        }
      });
    };

    connectWithPath(socketPath);

    // Full sync on join / reconnect (includes offline drivers with last position)
    socket.on('orderDriverSnapshot', ({ drivers: snap }) => {
      const updated = {};
      const positions = {};
      for (const d of snap) {
        updated[d.driver_id] = d;
        if (d.lat && d.lng) {
          positions[d.driver_id] = { lat: d.lat, lng: d.lng };
          prevGpsPos.current[d.driver_id] = { lat: d.lat, lng: d.lng };
        }
      }
      setDrivers((prev) => ({ ...prev, ...updated }));
      setMarkerPositions((prev) => ({ ...prev, ...positions }));
    });

    // Live GPS tick — animate marker
    socket.on('driverLocationUpdate', ({ driver_id, shipment_id, lat, lng, driver_name, last_updated }) => {
      setDrivers((prev) => ({
        ...prev,
        [driver_id]: { ...prev[driver_id], driver_id, shipment_id, lat, lng, driver_name, last_updated, is_live: true },
      }));
      const prev = prevGpsPos.current[driver_id];
      if (prev && (prev.lat !== lat || prev.lng !== lng)) {
        animateMarker(driver_id, prev.lat, prev.lng, lat, lng);
      } else if (!prev) {
        setMarkerPositions((mp) => ({ ...mp, [driver_id]: { lat, lng } }));
      }
      prevGpsPos.current[driver_id] = { lat, lng };
    });

    // Driver came online
    socket.on('driverJoined', ({ driver_id, shipment_id, driver_name }) => {
      setDrivers((prev) => ({
        ...prev,
        [driver_id]: {
          ...(prev[driver_id] || {}),
          driver_id, shipment_id, driver_name,
          lat: prev[driver_id]?.lat ?? null,
          lng: prev[driver_id]?.lng ?? null,
          last_updated: Date.now(),
          is_live: true,
        },
      }));
    });

    // Driver went offline — keep last position, mark as offline
    socket.on('driverLeft', ({ driver_id }) => {
      // Cancel any in-flight animation (position stays where it is)
      if (animFrames.current[driver_id]) {
        cancelAnimationFrame(animFrames.current[driver_id]);
        delete animFrames.current[driver_id];
      }
      setDrivers((prev) => {
        if (!prev[driver_id]) return prev;
        return { ...prev, [driver_id]: { ...prev[driver_id], is_live: false } };
      });
      // marker stays at last position — do NOT clear markerPositions or prevGpsPos
    });

    return () => {
      Object.values(animFrames.current).forEach(cancelAnimationFrame);
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, [orderId, animateMarker]);

  // ── Render ───────────────────────────────────────────────────────────────
  const driverEntries = Object.entries(drivers);
  const primaryDriver = driverEntries.length
    ? (drivers[String(selectedDriverId || driverEntries[0][0])] || driverEntries[0][1])
    : null;
  const primaryDriverName = primaryDriver?.driver_name || 'Driver';
  const primaryShipmentStatus = primaryDriver?.status || null;
  const mobileSteps = buildMobileSteps(orderStatus, primaryShipmentStatus);
  const deliveryText = deliveryAddress
    ? [
        deliveryAddress.area,
        deliveryAddress.line1,
        deliveryAddress.line2,
        [deliveryAddress.city, deliveryAddress.state, deliveryAddress.zipcode].filter(Boolean).join(' '),
        deliveryAddress.country,
      ].filter(Boolean).join(', ')
    : null;
  const primaryIsLive = !!primaryDriver?.is_live;
  const primaryLatLng = getValidLatLng(primaryDriver);
  const callPhone = String(deliveryAddress?.phone || '').trim();
  const callHref = callPhone ? `tel:${callPhone.replace(/[^\d+]/g, '')}` : null;

  const showOverlay = pageState !== 'live';
  const overlayText =
    pageState === 'loading' ? 'Loading order…' :
    pageState === 'planInactive' ? 'Live tracking not available for this store plan.' :
    pageState === 'notFound' ? 'Order not found. Check the link.' :
    pageState === 'error' ? 'Connection error. Pull to refresh / try again.' :
    '';

  return (
    <div style={S.root} className="clm-root">
      <style>{`
        .clm-root { background: #fff; }
        .clm-header { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:12px 14px; background:#fff; border-bottom:1px solid #f0f0f0; }
        .clm-title { font-size:15px; font-weight:800; color:#111827; line-height:1.2; }
        .clm-sub { font-size:12px; color:#6b7280; margin-top:2px; }
        .clm-badge { font-size:11px; font-weight:700; padding:4px 10px; border-radius:999px; white-space:nowrap; }
        .clm-mapWrap { flex:1; position:relative; min-height:0; }
        /* Leaflet uses high z-index panes; keep sheet above the map */
        .clm-mapWrap .leaflet-container { z-index: 0; }
        .clm-sheet { position:absolute; left:0; right:0; bottom:0; background:#fff; border-top-left-radius:18px; border-top-right-radius:18px; border-top:1px solid #f0f0f0; box-shadow:0 -8px 24px rgba(0,0,0,0.12); padding:10px 14px 14px; z-index: 1000; }
        .clm-grabber { width:44px; height:4px; border-radius:999px; background:#e5e7eb; margin:6px auto 10px; }
        .clm-driverRow { display:flex; align-items:center; gap:10px; }
        .clm-avatar { width:44px; height:44px; border-radius:999px; background:#111827; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:900; flex-shrink:0; }
        .clm-driverName { font-weight:800; color:#111827; font-size:14px; line-height:1.2; }
        .clm-driverMeta { font-size:12px; color:#6b7280; margin-top:2px; }
        .clm-actions { margin-left:auto; display:flex; gap:10px; }
        .clm-actionBtn { width:38px; height:38px; border-radius:999px; border:2px solid rgba(245,158,11,0.55); background:#fff; display:flex; align-items:center; justify-content:center; cursor:pointer; }
        .clm-actionBtn svg { width:18px; height:18px; color:#f59e0b; }
        .clm-stepsTitle { margin-top:12px; font-weight:800; color:#111827; font-size:13px; }
        .clm-step { display:flex; gap:10px; margin-top:10px; }
        .clm-dotWrap { width:18px; display:flex; justify-content:center; }
        .clm-dot { width:12px; height:12px; border-radius:999px; border:2px solid #f59e0b; background:#fff; margin-top:2px; }
        .clm-dot.done { background:#f59e0b; }
        .clm-dot.current { background: rgba(245,158,11,0.15); border-color: #f59e0b; box-shadow: 0 0 0 4px rgba(245,158,11,0.12); }
        .clm-line { width:2px; background:#f3f4f6; flex:1; margin-top:2px; }
        .clm-stepText { flex:1; }
        .clm-stepMain { font-weight:700; color:#111827; font-size:13px; }
        .clm-stepSub { font-size:11px; color:#6b7280; margin-top:2px; }
        .clm-primaryBtn { margin-top:14px; width:100%; height:46px; border-radius:12px; border:none; background:#f59e0b; color:#fff; font-weight:800; font-size:14px; }

        /* Keep bottom sheet visible on all screen sizes */
      `}</style>

      {/* Top bar (mobile friendly) */}
      <div className="clm-header">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => (window.history.length > 1 ? window.history.back() : window.close?.())}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 4,
                fontSize: 18,
                lineHeight: 1,
                cursor: 'pointer',
                color: '#111827',
              }}
              aria-label="Back"
            >
              ←
            </button>
            <div style={{ minWidth: 0 }}>
              <div className="clm-title">Order Tracking</div>
              <div className="clm-sub">
                Order #{rawOrderParam}{orderStatus ? ` · ${toTitleCaseStatus(orderStatus)}` : ''}
              </div>
            </div>
          </div>
        </div>
        <div
          className="clm-badge"
          style={{
            background: socketConnected ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: socketConnected ? '#16a34a' : '#dc2626',
          }}
        >
          {socketConnected ? 'Live' : 'Reconnecting'}
        </div>
      </div>

      {/* Map */}
      <div style={S.mapWrap} className="clm-mapWrap">
        <LeafletDriversMap
          center={DEFAULT_CENTER}
          zoom={12}
          driverEntries={driverEntries}
          markerPositions={markerPositions}
          selectedId={selectedDriverId}
          onSelect={setSelectedDriverId}
          truckIconUrl={truckImg}
          onMapCreated={onLeafletMapCreated}
          getOpacity={(driverId) => (drivers[String(driverId)]?.is_live ? 1 : 0.45)}
          renderPopup={(driverId) => {
            const key = String(driverId);
            const driver = drivers[key];
            const pos = markerPositions[key];
            if (!driver || !pos) return null;
            return (
              <div style={{ minWidth: 140 }}>
                <div style={S.infoName}>{driver.driver_name}</div>
                <div style={S.infoStatus(driver.is_live)}>
                  {driver.is_live ? '● Live' : `● Offline · last seen ${timeAgo(driver.last_updated)}`}
                </div>
                {driver.last_updated && (
                  <div style={S.infoTime}>
                    {driver.is_live ? 'Updated' : 'Last'}: {new Date(driver.last_updated).toLocaleTimeString()}
                  </div>
                )}
              </div>
            );
          }}
        />

        {driverEntries.length === 0 && (
          <div style={S.noDriversToast}>Waiting for driver updates…</div>
        )}

        {showOverlay && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(255,255,255,0.55)',
            pointerEvents: 'none',
          }}>
            <div style={{
              background: '#fff',
              border: '1px solid #f0f0f0',
              borderRadius: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              padding: '12px 14px',
              maxWidth: 360,
              textAlign: 'center',
              color: '#111827',
              fontWeight: 700,
              fontSize: 13,
            }}>
              {overlayText}
            </div>
          </div>
        )}

        {/* Bottom sheet (mobile) / driver list (desktop due to media query) */}
        <div className="clm-sheet">
          <div className="clm-grabber" />

          <div className="clm-driverRow">
            <div className="clm-avatar">{String(primaryDriverName || 'D').charAt(0).toUpperCase()}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="clm-driverName" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {primaryDriverName}
              </div>
              <div className="clm-driverMeta">
                {primaryShipmentStatus ? toTitleCaseStatus(primaryShipmentStatus) : 'Waiting for status'}
                {primaryDriver?.shipment_id ? ` · Shipment ${primaryDriver.shipment_id}` : ''}
              </div>
            </div>
            <div className="clm-actions">
              <div
                className="clm-actionBtn"
                title={callHref ? `Call ${callPhone}` : 'Call (no phone)'}
                role="button"
                tabIndex={0}
                style={{
                  opacity: callHref ? 1 : 0.4,
                  pointerEvents: callHref ? 'auto' : 'none',
                }}
                onClick={() => {
                  if (!callHref) return;
                  window.location.href = callHref;
                }}
                onKeyDown={(e) => {
                  if (!callHref) return;
                  if (e.key === 'Enter' || e.key === ' ') window.location.href = callHref;
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.81.3 1.6.54 2.36a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.72-1.06a2 2 0 0 1 2.11-.45c.76.24 1.55.42 2.36.54A2 2 0 0 1 22 16.92z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="clm-stepsTitle">Order Status</div>
          {mobileSteps.map((s, idx) => (
            <div className="clm-step" key={s.key}>
              <div className="clm-dotWrap">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                  <div className={`clm-dot ${s.done ? 'done' : (s.current ? 'current' : '')}`} />
                  {idx !== mobileSteps.length - 1 && <div className="clm-line" />}
                </div>
              </div>
              <div className="clm-stepText">
                <div className="clm-stepMain">{s.title}</div>
                {s.sub ? <div className="clm-stepSub">{s.sub}</div> : null}
              </div>
            </div>
          ))}

          {deliveryText ? (
            <div style={{ marginTop: 10 }}>
              <div className="clm-stepMain" style={{ fontWeight: 800 }}>Delivery Address</div>
              <div className="clm-stepSub">{deliveryText}</div>
            </div>
          ) : null}


        <button
            className="clm-primaryBtn"
            type="button"
            disabled={!primaryIsLive || !primaryLatLng}
            style={{
              opacity: (!primaryIsLive || !primaryLatLng) ? 0.55 : 1,
              cursor: (!primaryIsLive || !primaryLatLng) ? 'not-allowed' : 'pointer',
            }}
            onClick={() => {
              if (!primaryIsLive || !primaryLatLng) return;
              setSelectedDriverId(String(primaryDriver?.driver_id || driverEntries?.[0]?.[0]));
              focusOnLatLng(primaryLatLng);
            }}
          >
            Live Tracking
          </button>

          {/* Desktop-only: keep the old driver strip list for multi-driver scenarios */}
          {driverEntries.length > 1 && (
            <div style={{ marginTop: 12 }}>
              {driverEntries.map(([driverIdStr, driver]) => {
                const driverId = Number(driverIdStr);
                const ll = getValidLatLng(driver);
                const hasGps = !!ll;
                const isLive = !!driver.is_live;
                const initial = (driver.driver_name || 'D').charAt(0).toUpperCase();
                return (
                  <div
                    key={driverId}
                    style={S.driverCard(hasGps)}
                    onClick={() => {
                      setSelectedDriverId(String(driverId));
                      focusOnLatLng(ll);
                    }}
                  >
                    <div style={S.avatar(isLive)}>{initial}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.cardName}>
                        {driver.driver_name}
                        {isLive ? <span style={S.liveBadge}>Live</span> : <span style={S.offlineBadge}>Offline</span>}
                      </div>
                      {driver.status ? <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{toTitleCaseStatus(driver.status)}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerLiveMap;
